// Client-side pricing types + estimate helpers. Mirrors api/src/lib/pricing.ts.
// The studio shows an ESTIMATE; the superadmin's quote is always authoritative.

export interface PrintSizeTier {
  id: string;
  label: string;
  maxWidthCm: number;
  maxHeightCm: number;
  price: number;
}

export interface Pricing {
  currency: string;
  tshirt: { garmentPrice: number };
  gangsheet: { price: number };
  printSizes: PrintSizeTier[];
  delivery: { courier: number; pudo: number };
}

export const DEFAULT_PRICING: Pricing = {
  currency: "R",
  tshirt: { garmentPrice: 120 },
  gangsheet: { price: 250 },
  printSizes: [
    { id: "small", label: "Small — up to A5 (15×21cm)", maxWidthCm: 15, maxHeightCm: 21, price: 45 },
    { id: "medium", label: "Medium — up to A4 (21×30cm)", maxWidthCm: 21, maxHeightCm: 30, price: 75 },
    { id: "large", label: "Large — up to A3 (30×42cm)", maxWidthCm: 30, maxHeightCm: 42, price: 120 },
  ],
  delivery: { courier: 99, pudo: 60 },
};

// Smallest tier whose limits contain the print; falls back to the largest tier.
export function pickTier(pricing: Pricing, wCm: number, hCm: number): PrintSizeTier | null {
  const sorted = [...pricing.printSizes].sort((a, b) => a.price - b.price);
  return sorted.find((t) => wCm <= t.maxWidthCm && hCm <= t.maxHeightCm) ?? sorted[sorted.length - 1] ?? null;
}

export type PrintMode = "print_on_garment" | "print_only";

// Unit price for one item given its kind, print size and mode.
export function estimateUnit(
  pricing: Pricing,
  opts: { kind: "tshirt" | "gangsheet"; printMode: PrintMode; wCm: number; hCm: number }
): { unit: number; tier: PrintSizeTier | null } {
  if (opts.kind === "gangsheet") return { unit: pricing.gangsheet.price, tier: null };
  const tier = pickTier(pricing, opts.wCm, opts.hCm);
  const print = tier ? tier.price : 0;
  const garment = opts.printMode === "print_on_garment" ? pricing.tshirt.garmentPrice : 0;
  return { unit: print + garment, tier };
}
